// ui/skillHeader.js
// Reusable skill header component with stats and XP progress

import { gameState } from '../game/gameState.js';
import { XP_TABLE, GAME_CONFIG } from '../game/constants.js';
import { formatWithCommas } from '../utils/format.js';
import { getXPPerHour, isMaxLevel, isMaxVirtualLevel } from '../systems/experience.js';

/**
 * Get display values for skill header based on level and virtual levels setting
 */
function getSkillDisplayValues(skill, skillId) {
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    if (isMax && !virtualEnabled) {
        // At max level without virtual levels - show mastered state
        return {
            levelDisplay: `${skill.level} (Max)`,
            nextLevelXP: 'Mastered',
            remainingXP: '—',
            progressPercent: 100,
            progressText: 'Max Level Reached',
            showProgress: false
        };
    } else if (isMax && virtualEnabled) {
        // At max level with virtual levels - show virtual progress
        const displayLevel = skill.virtualLevel;
        const nextLevelXP = isVirtualMax ? XP_TABLE[GAME_CONFIG.VIRTUAL_LEVEL_CAP - 1] : XP_TABLE[displayLevel];
        const progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);

        return {
            levelDisplay: `${skill.level} (${skill.virtualLevel})`,
            nextLevelXP: isVirtualMax ? 'Virtual Max' : formatWithCommas(nextLevelXP),
            remainingXP: isVirtualMax ? '—' : formatWithCommas(skill.nextLevelXp - skill.currentXp),
            progressPercent: progressPercent,
            progressText: isVirtualMax
                ? 'Virtual Max Level Reached'
                : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)} XP`,
            showProgress: !isVirtualMax
        };
    } else {
        // Normal leveling
        return {
            levelDisplay: skill.level,
            nextLevelXP: formatWithCommas(XP_TABLE[skill.level]),
            remainingXP: formatWithCommas(skill.nextLevelXp - skill.currentXp),
            progressPercent: (skill.currentXp / skill.nextLevelXp * 100),
            progressText: `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)} XP`,
            showProgress: true
        };
    }
}

/**
 * Render the skill header with stats and XP progress bar
 * @param {string} skillId - The skill ID from SKILLS enum
 * @param {string} icon - Emoji icon for the skill
 * @param {string} name - Display name for the skill
 * @returns {string} HTML string for the skill header
 */
export function renderSkillHeader(skillId, icon, name) {
    const skill = gameState.getSkill(skillId);
    const xpPerHour = getXPPerHour(skillId);
    const display = getSkillDisplayValues(skill, skillId);

    return `
      <div class="skill-header">
        <h2>${icon} ${name}</h2>
        <div class="skill-info-grid">
          <div class="stat-box">
            <span class="stat-label">Level</span>
            <span class="stat-value">${display.levelDisplay}</span>
          </div>
          <div class="stat-box">
            <span class="stat-label">Total XP / Next Level</span>
            <span class="stat-value">${formatWithCommas(skill.xp)} / ${display.nextLevelXP}</span>
          </div>
          <div class="stat-box">
            <span class="stat-label">Remaining XP</span>
            <span class="stat-value">${display.remainingXP}</span>
          </div>
          <div class="stat-box">
            <span class="stat-label">XP/h</span>
            <span class="stat-value xp-per-hour">${formatWithCommas(xpPerHour)}</span>
          </div>
        </div>
        <div class="xp-progress-container">
          <div class="xp-progress-bar">
            <div class="xp-progress-fill" style="width: ${display.progressPercent}%"></div>
          </div>
          <div class="xp-progress-text">${display.progressText}</div>
        </div>
      </div>
    `;
}

/**
 * Update the skill header stats (call on each tick for active skill)
 * @param {string} skillId - The skill ID from SKILLS enum
 * @param {string} containerSelector - CSS selector for the skill interface container
 */
export function updateSkillHeader(skillId, containerSelector) {
    const skill = gameState.getSkill(skillId);
    const xpPerHour = getXPPerHour(skillId);
    const display = getSkillDisplayValues(skill, skillId);

    // Update stat boxes
    const statBoxes = document.querySelectorAll(`${containerSelector} .stat-box .stat-value`);
    if (statBoxes.length >= 4) {
        statBoxes[0].textContent = display.levelDisplay;
        statBoxes[1].textContent = `${formatWithCommas(skill.xp)} / ${display.nextLevelXP}`;
        statBoxes[2].textContent = display.remainingXP;
        statBoxes[3].textContent = formatWithCommas(xpPerHour);
    }

    // Update XP progress bar
    const xpProgressFill = document.querySelector(`${containerSelector} .xp-progress-fill`);
    if (xpProgressFill) {
        xpProgressFill.style.width = `${display.progressPercent}%`;
    }

    // Update XP progress text
    const xpProgressText = document.querySelector(`${containerSelector} .xp-progress-text`);
    if (xpProgressText) {
        xpProgressText.textContent = display.progressText;
    }
}

export default {
    renderSkillHeader,
    updateSkillHeader,
};
